import { useState } from 'react';

// Third-party
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';

// Components
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';

import moment from 'moment-timezone';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { TCustomTheme } from '@mgm/common/theme';

import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseBreadcrumbs, SoftButton, BaseButtonMenu } from '@mgm/common/components/base';
import { useGetScheduleDataAPI } from '../../hooks/api/useSchedules';
import { DeleteShiftDialog, CreateEditScheduleShift, ShiftNotesNav } from './Shared/components';
import { Schedule } from './Schedule';
import { useSchedulePermissions, useCommonSchedules } from './Shared/hooks';
import { DayOffNav, DeleteTimeOffDialog } from './Calendar/components';
import { Calendar } from './Calendar';
import { useAppDispatch } from '../../hooks/useAppStore';
import { schedulesActions } from '../../store/schedules/slice';
import { CopyScheduleNav } from './Schedule/components';

const SchedulesView = () => {
  const {
    addShiftItems, addTimeoff, sendToStaff, isStaffLoading,
  } = useCommonSchedules();
  const { t } = useTranslation(['timesheets', 'schedule', 'team']);
  const theme = useTheme() as TCustomTheme;

  useGetScheduleDataAPI({ isMainSchedulesPage: true });

  const dispatch = useAppDispatch();
  const { isMobile } = useCustomTheme(theme);

  const [selectedDate, setSelectedDate] = useState(moment());

  const { schedulePermissions } = useSchedulePermissions();

  const openCopyShiftsNav = () => {
    dispatch(schedulesActions.setIsOpenCopyShiftsNav(true));
  };

  return (
    <Box>
      <BaseBreadcrumbs
        heading={t('workSchedule', { ns: 'nav' })}
        links={[
          { name: t('schedules', { ns: 'nav' }), href: '/schedules/weekly' },
          { name: t('workSchedule', { ns: 'nav' }), href: '/schedules/weekly' },
        ]}
        {...(schedulePermissions.isAllowManage && !isMobile && {
          action: (
            <Stack direction="row" spacing={2}>
              <SoftButton
                id="copy-schedule"
                onClick={openCopyShiftsNav}
                background={theme.palette.primary.main}
              >
                {t('copySchedule', { ns: 'schedule' })}
              </SoftButton>
              <Button onClick={addTimeoff} variant="outlined">
                {t('timeoff', { ns: 'schedule' })}
              </Button>
              <LoadingButton loading={isStaffLoading} onClick={() => sendToStaff(selectedDate)} variant="outlined">
                {t('sendToStaff', { ns: 'schedule' })}
              </LoadingButton>
              <BaseButtonMenu
                isRemoveBtnHover={false}
                buttonProps={{
                  children: t('addShift'),
                  endIcon: (
                    <Icon path={mdiChevronDown} size={0.8} />
                  ),
                  variant: 'contained',
                }}
                listProps={{
                  items: addShiftItems,
                  sx: {
                    width: 125,
                  },
                  itemfontsize: 12,
                }}
              />
            </Stack>
          ),
        })}
      />

      {isMobile ? <Calendar /> : (
        <Schedule
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
        />
      )}

      <CreateEditScheduleShift />
      <DeleteShiftDialog />
      <DeleteTimeOffDialog />
      <DayOffNav />
      <ShiftNotesNav />
      <CopyScheduleNav />
    </Box>
  );
};

export {
  SchedulesView,
};
